import md5 from 'md5';
import type { Asset, Entry, UnresolvedLink } from 'contentful';
import {
  ComponentPropertyValue,
  DesignValue,
  ExperienceComponentSettings,
  ExperienceComponentTree,
  ExperienceDataSource,
  ExperienceUnboundValues,
  PrimitiveValue,
} from '@contentful/experiences-validators';
import { buildCfStyles, stringifyCssProperties } from './stylesUtils';
// import {
import { checkIsAssemblyNode, getTargetValueInPixels, parseCSSValue } from '@/utils/utils';
import { isValidBreakpointValue, mergeDesignValuesByBreakpoint } from '../breakpoints';
import { toMediaQuery } from './toMediaQuery';
import { transformVisibility } from './styleTransformers';
import { debug } from '@/utils/debugLogger';
import { builtInStyles, optionalBuiltInStyles } from '@/definitions';
import { designTokensRegistry } from '@/registries';
import {
  BackgroundImageOptions,
  ComponentTreeNode,
  DesignTokensDefinition,
  Experience,
  StyleProps,
} from '@/types';
import { CF_STYLE_ATTRIBUTES, SUPPORTED_IMAGE_FORMATS } from '@/constants';
import { getOptimizedBackgroundImageAsset } from '@/utils/transformers/media/getOptimizedBackgroundImageAsset';
import { AssetDetails, AssetFile } from 'contentful';

type FlattenedDesignTokens = Record<
  string,
  string | { width?: string; style?: string; color?: string }
>;

export const detachExperienceStyles = (experience: Experience): string | undefined => {
  const experienceTreeRoot = experience.entityStore?.experienceEntryFields
    ?.componentTree as ExperienceComponentTree;

  if (!experienceTreeRoot) {
    return;
  }

  const isRenderingAPatternEntry = experience.entityStore?.isExperienceAPatternEntry;

  const mapOfDesignVariableKeys = flattenDesignTokenRegistry(designTokensRegistry);

  // getting breakpoints from the entry componentTree field
  const { breakpoints } = experienceTreeRoot;

  // creating the structure which I thought would work best for aggregation
  const mediaQueryDataByBreakpoint = breakpoints.reduce(
    (mediaQueryTemplate, breakpoint) => ({
      ...mediaQueryTemplate,
      [breakpoint.id]: {
        condition: breakpoint.query,
        cssByClassName: {},
        visibilityCssByClassName: {},
      },
    }),
    {} as Record<
      string,
      {
        condition: string;
        cssByClassName: Record<string, string>;
        visibilityCssByClassName: Record<string, string>;
      }
    >,
  );

  // getting the breakpoint ids
  const breakpointIds = Object.keys(mediaQueryDataByBreakpoint);

  const iterateOverTreeAndExtractStyles = ({
    componentTree,
    dataSource,
    unboundValues,
    componentSettings,
    componentVariablesOverwrites,
    patternWrapper,
    wrappingPatternIds,
    wrappingPatternNodeIds = isRenderingAPatternEntry ? ['root'] : [],
  }: {
    componentTree: ExperienceComponentTree;
    dataSource: ExperienceDataSource;
    unboundValues: ExperienceUnboundValues;
    componentSettings?: ExperienceComponentSettings;
    componentVariablesOverwrites?: Record<string, ComponentPropertyValue>;
    patternWrapper?: ComponentTreeNode;
    wrappingPatternIds: Set<string>;
    wrappingPatternNodeIds?: string[];
  }) => {
    // traversing the tree
    const queue: ComponentTreeNode[] = [];

    queue.push(...componentTree.children);

    // for each tree node
    while (queue.length) {
      const currentNode = queue.shift();
      if (!currentNode) {
        break;
      }

      const usedComponents = experience.entityStore?.usedComponents ?? [];

      const isPatternNode = checkIsAssemblyNode({
        componentId: currentNode.definitionId,
        usedComponents,
      });

      if (isPatternNode) {
        // When detecting a circular dependency among patterns, stop to avoid an infinite loop
        if (wrappingPatternIds.has(currentNode.definitionId)) {
          continue;
        }

        const patternEntry = usedComponents.find(
          (component) => component.sys.id === currentNode!.definitionId,
        );

        if (!patternEntry || !('fields' in patternEntry)) {
          continue;
        }

        const nextComponentVariablesOverwrites = resolveComponentVariablesOverwrites({
          patternNode: currentNode,
          wrapperComponentVariablesOverwrites: componentVariablesOverwrites,
          wrapperComponentSettings: componentSettings,
        });

        // the node of a used pattern contains only the definitionId (id of the patter entry)
        // as well as the variables overwrites
        // the layout of a pattern is stored in it's entry
        iterateOverTreeAndExtractStyles({
          // that is why we pass it here to iterate of the pattern tree
          componentTree: patternEntry.fields.componentTree,
          // but we pass the data source of the experience entry cause that's where the binding is stored
          dataSource,
          // unbound values of a pattern store the default values of pattern variables
          unboundValues: { ...unboundValues, ...patternEntry.fields.unboundValues },
          // this is where we can map the pattern variable to it's default value
          componentSettings: patternEntry.fields.componentSettings,
          // and this is where the over-writes for the default values are stored
          // yes, I know, it's a bit confusing
          componentVariablesOverwrites: nextComponentVariablesOverwrites,
          // pass top-level pattern node to store instance-specific child styles for rendering
          patternWrapper: currentNode,
          wrappingPatternIds: new Set([...wrappingPatternIds, currentNode.definitionId]),
          wrappingPatternNodeIds: [...wrappingPatternNodeIds, currentNode.id || ''],
        });
        continue;
      }

      /* [Data format] `currentNode.variables` uses the following serialized shape:
       * {
       *   cfMargin: { type: 'DesignValue', valuesByBreakpoint: { desktop: '1px', tablet: '2px' } },
       *   cfPadding: { type: 'DesignValue', valuesByBreakpoint: { desktop: '3px' } }
       *   cfBackgroundImageUrl: { type: 'BoundValue', path: '/lUERH7tX7nJTaPX6f0udB/fields/assetReference/~locale/fields/file/~locale' }
       *   asdf1234: { type: 'ComponentValue', key: 'qwer567' }
       *   // ...
       * }
       */

      // so first, I convert it into a map to help me make it easier to access the values
      const propsByBreakpoint = indexByBreakpoint({
        variables: currentNode.variables,
        breakpointIds,
        unboundValues: unboundValues,
        dataSource: dataSource,
        componentSettings,
        componentVariablesOverwrites,
        getBoundEntityById: (id: string) => {
          return experience.entityStore?.entities.find(
            (entity: Entry | Asset) => entity.sys.id === id,
          );
        },
      });

      // When the node is hidden for any breakpoint, we need to handle this separately with a disjunct media query.
      const isAnyVisibilityValueHidden = Object.values(propsByBreakpoint).some(
        (designProperties) => designProperties.cfVisibility === false,
      );
      // We always need an explicit value when using disjunct media queries
      // Example: desktop uses "false" and tablet is undefined -> we need to set `display: none` for tablet as well.
      let previousVisibilityValue: boolean | undefined = undefined;

      /* [Data format] `propsByBreakpoint` is a map of "breakpointId > propertyName > plainValue":
       * {
       *   desktop: {
       *     cfMargin: '1px',
       *     cfWidth: '100%',
       *     cfBackgroundImageUrl: 'https://example.com/image.jpg'
       *     //...
       *   }
       * }
       */

      const currentNodeClassNames: string[] = [];
      // Chain IDs to avoid overwriting styles across multiple instances of the same pattern
      // e.g. `{outerPatternNodeId}{innerPatternNodeId}-{currentNodeId}`
      // (!) Notice that the chain of patterns (before the dash) follows the format of prebinding/ parameters
      const currentNodeIdsChain = [...wrappingPatternNodeIds, currentNode.id].join('-');

      // For each breakpoint, resolve design tokens, create the CSS and generate a unique className.
      for (const breakpointId of breakpointIds) {
        const propsByBreakpointWithResolvedDesignTokens = Object.entries(
          propsByBreakpoint[breakpointId],
        ).reduce((acc, [variableName, variableValue]) => {
          return {
            ...acc,
            [variableName]: maybePopulateDesignTokenValue(
              variableName,
              variableValue,
              mapOfDesignVariableKeys,
            ),
          };
        }, {} as Partial<StyleProps>);

        // Convert CF-specific property names to CSS variables, e.g. `cfMargin` -> `margin`
        const cfStyles = buildCfStyles(propsByBreakpointWithResolvedDesignTokens);

        /* [Data format] `cfStyles` is a list of CSSProperties (React format):
         * {
         *   margin: '1px',
         *   width: '100%',
         *   backgroundImage: 'url(https://example.com/image.jpg)'
         *   //...
         * }
         */
        const generatedCss = stringifyCssProperties(cfStyles);

        if (!generatedCss && !isAnyVisibilityValueHidden) {
          // If there are no styles to apply, skip this breakpoint completely including the class name
          continue;
        }

        /* [Data format] `generatedCss` is the minimized CSS string that will be added to the DOM:
         * generatedCss = "margin: 1px;width: 100%;..."
         */

        // - Adding breakpointId to ensure not using the same IDs between breakpoints as this leads to
        // conflicts between different breakpoint values from multiple nodes where the hash would be equal
        // - Adding wrapping pattern nodes IDs to avoid conflicts between similar nested patterns as those
        // could override each others CSS for some breakpoints just through the order of `<style>` tags in the DOM.
        // - We're only considering the regular CSS (not the visibility-specific one) but that doesn't cause an issue
        // since we already use the breakpointId (which is not used in CSR, see createStylesheetsForBuiltInStyles).
        // Theoretically, we could drop `generatedCss` from the hash generation and still be safe from conflicts (unless
        // the node IDs are similar but visibility is different?)
        const styleHash = md5(currentNodeIdsChain + breakpointId + generatedCss);

        // and prefix the className to make sure the value can be processed
        const className = `cf-${styleHash}`;

        // I save the generated hashes into an array to later save it in the tree node
        // as cfSsrClassName prop
        // making sure to avoid the duplicates in case styles for > 1 breakpoints are the same
        if (!currentNodeClassNames.includes(className)) {
          currentNodeClassNames.push(className);
        }

        // Only if the hash was not used yet, save the CSS to the stylesheet
        if (!mediaQueryDataByBreakpoint[breakpointId].cssByClassName[className]) {
          mediaQueryDataByBreakpoint[breakpointId].cssByClassName[className] = generatedCss;
        }

        // Special case for visibility to override any custom `display` values but only for a specific breakpoint.
        if (isAnyVisibilityValueHidden) {
          const visibilityValue =
            propsByBreakpointWithResolvedDesignTokens.cfVisibility ?? previousVisibilityValue;
          previousVisibilityValue = visibilityValue;
          const visibilityStyles = transformVisibility(visibilityValue);
          const visibilityCss = stringifyCssProperties(visibilityStyles);
          if (!mediaQueryDataByBreakpoint[breakpointId].visibilityCssByClassName[className]) {
            mediaQueryDataByBreakpoint[breakpointId].visibilityCssByClassName[className] =
              visibilityCss;
          }
        }
      }

      // all generated classNames are saved in the tree node
      // to be handled by the sdk later
      // each node will get N classNames, where N is the number of breakpoints
      // browsers process classNames in the order they are defined
      // meaning that in case of className1 className2 className3
      // className3 will win over className2 and className1
      // making sure that we respect the order of breakpoints from
      // we can achieve "desktop first" or "mobile first" approach to style over-writes
      if (patternWrapper) {
        // @ts-expect-error -- valueByBreakpoint is not explicitly defined, but it's already defined in the patternWrapper styles
        patternWrapper.variables.cfSsrClassName = {
          ...(patternWrapper.variables.cfSsrClassName ?? {}),
          type: 'DesignValue',
          [currentNodeIdsChain]: {
            valuesByBreakpoint: {
              [breakpointIds[0]]: currentNodeClassNames.join(' '),
            },
          },
        };
      } else {
        currentNode.variables.cfSsrClassName = {
          type: 'DesignValue',
          valuesByBreakpoint: {
            [breakpointIds[0]]: currentNodeClassNames.join(' '),
          },
        };
      }

      queue.push(...currentNode.children);
    }
  };

  iterateOverTreeAndExtractStyles({
    componentTree: experienceTreeRoot,
    dataSource: experience.entityStore?.dataSource ?? {},
    unboundValues: experience.entityStore?.unboundValues ?? {},
    componentSettings: experience.entityStore?.experienceEntryFields?.componentSettings,
    wrappingPatternIds: new Set(
      experience.entityStore?.experienceEntryId ? [experience.entityStore.experienceEntryId] : [],
    ),
  });

  // once the whole tree was traversed, for each breakpoint, I aggregate the styles
  // for each generated className into one css string
  return Object.values(mediaQueryDataByBreakpoint).reduce(
    (acc, { condition, cssByClassName, visibilityCssByClassName }, index) => {
      const mediaQueryCss = toMediaQuery({ cssByClassName, condition });

      // Handle visibility separately to use disjunct media queries ("if desktop but not tablet ...")
      // Enables to hide on one breakpoint but render any unknown custom `display` value on another breakpoint.
      const visibilityMediaQueryCss = toMediaQuery({
        cssByClassName: visibilityCssByClassName,
        condition,
        // Breakpoint validation ensures that it starts with the '*' breakpoint
        nextCondition: Object.values(mediaQueryDataByBreakpoint)[index + 1]?.condition,
      });

      return `${acc}${mediaQueryCss}${visibilityMediaQueryCss}`;
    },
    '',
  );
};

/**
 * Rendering pattern nodes inside pattern entry by injecting default values from the top:
 * When there is a ComponentValue but the recursive logic is not wrapped by a pattern node (no `componentVariablesOverwrites`),
 * a pattern entry is rendered directly. In this case, we replace each ComponentValue with the default value from componentSettings.
 */
const injectDefaultValuesForComponentValues = ({
  patternNode,
  wrapperComponentSettings,
}: {
  patternNode: ComponentTreeNode;
  wrapperComponentSettings?: ExperienceComponentSettings;
}): Record<string, ComponentPropertyValue> => {
  const propertyDefinitions = wrapperComponentSettings?.variableDefinitions;
  return Object.entries(patternNode.variables).reduce(
    (resolvedProperties, [propertyName, propertyValue]) => {
      if (propertyValue.type === 'ComponentValue') {
        const componentValueKey = propertyValue.key;
        const componentDefaultValue = propertyDefinitions?.[componentValueKey].defaultValue;
        // We're only considering design properties for styles generation
        if ((componentDefaultValue as DesignValue)?.type === 'DesignValue') {
          resolvedProperties[propertyName] = componentDefaultValue;
        }
      } else {
        // Do nothing when it's not a ComponentValue & just keep the defined value
        resolvedProperties[propertyName] = propertyValue;
      }
      return resolvedProperties;
    },
    {},
  );
};

/**
 * In case of nested patterns, we need to resolve the ComponentValue properties and overwrite them with the value
 * stored in the parent component.
 *
 *
 * @param patternNode - pattern node which contains the variables
 * @param componentVariablesOverwrites - object which contains the variables of the parent component
 */
const resolveComponentVariablesOverwrites = ({
  patternNode,
  wrapperComponentVariablesOverwrites,
  wrapperComponentSettings,
}: {
  patternNode: ComponentTreeNode;
  wrapperComponentVariablesOverwrites?: Record<string, ComponentPropertyValue>;
  wrapperComponentSettings?: ExperienceComponentSettings;
}): Record<string, ComponentPropertyValue> => {
  // In case of rendering a pattern entry, there are no custom ComponentValues.
  // So we pass down the default values from this pattern node down to each deeper pattern level.
  if (!wrapperComponentVariablesOverwrites) {
    return injectDefaultValuesForComponentValues({
      patternNode,
      wrapperComponentSettings,
    });
  }

  // Rendering (nested) pattern node inside another pattern node (for both experience & pattern entry):
  // The `wrapperComponentVariablesOverwrites` from the top-most pattern node is passed through to each child
  // node (and nested pattern nodes). It replaces each ComponentValue in the subtree with either the overwrite
  // or the default value.
  return Object.entries(patternNode?.variables).reduce(
    (resolvedValues, [propertyName, propertyValue]) => {
      if (propertyValue.type === 'ComponentValue') {
        // Copying the values from the parent node
        const overwritingValue = wrapperComponentVariablesOverwrites?.[propertyValue.key];
        // Property definition from the parent pattern
        const propertyDefinition =
          wrapperComponentSettings?.variableDefinitions?.[propertyValue.key];
        const defaultValue = propertyDefinition?.defaultValue as ComponentPropertyValue | undefined;
        // The overwriting value is either a custom value from the experience or default value from a
        // wrapping pattern node that got trickled down to this nesting level.
        resolvedValues[propertyName] = mergeDefaultAndOverwriteValues(
          defaultValue,
          overwritingValue,
        );
      } else {
        // Keep raw values
        resolvedValues[propertyName] = propertyValue;
      }
      return resolvedValues;
    },
    {},
  );
};

export const isCfStyleAttribute = (variableName: string): variableName is keyof StyleProps => {
  return CF_STYLE_ATTRIBUTES.includes(variableName);
};

export const maybePopulateDesignTokenValue = (
  variableName: string,
  variableValue: unknown,
  mapOfDesignVariableKeys: FlattenedDesignTokens,
) => {
  // TODO: refactor to reuse fn from core package
  if (typeof variableValue !== 'string') {
    return variableValue;
  }

  if (!isCfStyleAttribute(variableName)) {
    return variableValue;
  }

  // matches ${...} and captures the content between ${ and }
  // ${color.Blue}, ${spacing.Sizes.Large} or ${border.Text Heading.Small}
  const templateStringRegex = /\$\{(\w[^}]*)}/g;

  const result = variableValue
    .replace(templateStringRegex, (_: string, rawKey: string): string => {
      const value = mapOfDesignVariableKeys[rawKey];

      if (!value) {
        if (builtInStyles[variableName]?.defaultValue) {
          return String(builtInStyles[variableName]!.defaultValue);
        }
        if (optionalBuiltInStyles[variableName]?.defaultValue) {
          return String(optionalBuiltInStyles[variableName]!.defaultValue);
        }

        return '0px';
      }
      if (variableName === 'cfBorder' || variableName.startsWith('cfBorder_')) {
        if (typeof value === 'object') {
          const { width, style, color } = value;
          return `${width} ${style} ${color}`;
        }
      }
      return String(value);
    })
    // Replace all multiple spaces with a single space
    .replace(/  +/g, ' ');
  return result;
};

const transformMedia = (boundAsset: Asset, width?: string, options?: BackgroundImageOptions) => {
  try {
    const asset = boundAsset as Asset;
    // Target width (px/rem/em) will be applied to the css url if it's lower than the original image width (in px)
    const assetDetails = asset.fields.file?.details as AssetDetails;

    const assetWidth = assetDetails?.image?.width || 0; // This is always in px
    if (!options) {
      return asset.fields.file?.url as string;
    }
    const targetWidthObject = parseCSSValue(options.targetSize); // Contains value and unit (px/rem/em) so convert and then compare to assetWidth
    const targetValue = targetWidthObject ? getTargetValueInPixels(targetWidthObject) : assetWidth;

    if (targetValue < assetWidth) width = `${targetValue}px`;
    return getOptimizedBackgroundImageAsset(
      asset.fields.file as AssetFile,
      width as string,
      options.quality,
      options.format as (typeof SUPPORTED_IMAGE_FORMATS)[number],
    );
  } catch (error) {
    debug.error('[experiences-core::ssrStyles] Error transforming image asset', error);
  }
  return boundAsset.fields.file?.url as string;
};

export const resolveBackgroundImageBinding = ({
  variableData,
  getBoundEntityById,
  dataSource = {},
  unboundValues = {},
  componentVariablesOverwrites,
  componentSettings = { variableDefinitions: {} },
  options,
  width,
}: {
  variableData: ComponentPropertyValue;
  getBoundEntityById: (id: string) => Entry | Asset | undefined;
  unboundValues?: ExperienceUnboundValues;
  dataSource?: ExperienceDataSource;
  componentSettings?: ExperienceComponentSettings;
  // patternNode.variables - a place which contains bindings scoped to the pattern
  componentVariablesOverwrites?: Record<string, ComponentPropertyValue>;
  options?: BackgroundImageOptions;
  width?: string;
}) => {
  if (variableData.type === 'UnboundValue') {
    const uuid = variableData.key;
    return unboundValues[uuid]?.value as string;
  }

  if (variableData.type === 'ComponentValue') {
    const variableDefinitionKey = variableData.key;
    const variableDefinition = componentSettings.variableDefinitions[variableDefinitionKey];

    // @ts-expect-error TODO: Types coming from validations erroneously assume that `defaultValue` can be a primitive value (e.g. string or number)
    const defaultValueKey = variableDefinition.defaultValue?.key;
    const defaultValue = unboundValues[defaultValueKey].value;

    const overwriteValue = componentVariablesOverwrites?.[variableDefinitionKey];

    // overwriteValue is a ComponentValue we can safely return the default value
    if (!overwriteValue || overwriteValue.type === 'ComponentValue') {
      return defaultValue as string | undefined;
    }

    // at this point overwriteValue will either be type of 'DesignValue' or 'BoundValue'
    // so we recursively run resolution again to resolve it
    const resolvedValue = resolveBackgroundImageBinding({
      variableData: overwriteValue,
      getBoundEntityById,
      dataSource,
      unboundValues,
      componentVariablesOverwrites,
      componentSettings,
      options,
      width,
    });

    return resolvedValue || (defaultValue as string | undefined);
  }

  if (variableData.type === 'BoundValue') {
    // '/lUERH7tX7nJTaPX6f0udB/fields/assetReference/~locale/fields/file/~locale'
    const [, uuid] = variableData.path.split('/');
    const binding = dataSource[uuid] as UnresolvedLink<'Entry' | 'Asset'> | undefined;
    // Safeguard against edge cases - we should not have bound style values that do not have a data source entry.
    // However, just in case, we handle it here as empty and allow the user to replace without errors.
    if (!binding) {
      return;
    }

    const boundEntity = getBoundEntityById(binding.sys.id);
    if (!boundEntity) {
      return;
    }

    if (boundEntity.sys.type === 'Asset') {
      return transformMedia(boundEntity as Asset, width, options);
    } else {
      // '/lUERH7tX7nJTaPX6f0udB/fields/assetReference/~locale/fields/file/~locale'
      // becomes
      // '/fields/assetReference/~locale/fields/file/~locale'
      const pathWithoutUUID = variableData.path.split(uuid)[1];
      // '/fields/assetReference/~locale/fields/file/~locale'
      // becomes
      // '/fields/assetReference/'
      const pathToReferencedAsset: string = pathWithoutUUID.split('~locale')[0];
      // '/fields/assetReference/'
      // becomes
      // '[fields, assetReference]'
      const [, fieldName] = pathToReferencedAsset.substring(1).split('/') ?? undefined;

      const referenceToAsset = (boundEntity as Entry).fields[fieldName] as
        | UnresolvedLink<'Asset'>
        | undefined;

      if (!referenceToAsset) {
        return;
      }

      if (referenceToAsset.sys?.linkType === 'Asset') {
        const referencedAsset = getBoundEntityById(referenceToAsset.sys.id) as Asset | undefined;
        if (!referencedAsset) {
          return;
        }

        return transformMedia(referencedAsset as Asset, width, options);
      }
    }
  }
};

const resolveVariable = ({
  variableData,
  defaultBreakpoint,
  componentSettings = { variableDefinitions: {} },
  componentVariablesOverwrites,
}: {
  variableData: ComponentPropertyValue;
  defaultBreakpoint: string;
  componentSettings?: ExperienceComponentSettings;
  componentVariablesOverwrites?: Record<string, ComponentPropertyValue>;
}) => {
  if (variableData?.type === 'DesignValue') {
    return variableData.valuesByBreakpoint[defaultBreakpoint] || {};
  } else if (variableData?.type === 'ComponentValue') {
    const variableDefinitionKey = variableData.key;
    const variableDefinition = componentSettings.variableDefinitions[variableDefinitionKey];
    const defaultValue = variableDefinition.defaultValue;
    const userSetValue = componentVariablesOverwrites?.[variableDefinitionKey];
    if (!userSetValue || userSetValue.type === 'ComponentValue') {
      return (defaultValue as DesignValue)?.valuesByBreakpoint[defaultBreakpoint] || '';
    }
    return resolveVariable({
      variableData: userSetValue,
      defaultBreakpoint,
      componentSettings,
      componentVariablesOverwrites,
    });
  }
};

/**
 * Takes the initial set of properties, filters only design properties that will be mapped to CSS and
 * re-organizes them to be indexed by breakpoint ID ("breakpoint > variable > value"). It will
 * also resolve the design/ component values to plain values.
 *
 * **Example Input**
 * ```
 * variables = {
 *   cfMargin: { type: 'DesignValue', valuesByBreakpoint: { desktop: '1px', tablet: '2px' } },
 *   cfPadding: { type: 'DesignValue', valuesByBreakpoint: { desktop: '3px', mobile: '4px' } }
 * }
 * ```
 *
 * **Example Output**
 * ```
 * variableValuesByBreakpoints = {
 *   desktop: {
 *     cfMargin: '1px',
 *     cfPadding: '3px'
 *   },
 *   tablet: {
 *     cfMargin: '2px'
 *   },
 *   mobile: {
 *    cfPadding: '4px'
 *   }
 * }
 * ```
 *
 * **Note**
 * - The property cfBackgroundImageUrl is the only content property that gets mapped to CSS as well.
 *   It will be solely stored on the default breakpoint.
 * - For ComponentValues, it will either take the override from the pattern instance or fallback to
 *   the defaultValue defined in variableDefinitions.
 */
export const indexByBreakpoint = ({
  variables,
  breakpointIds,
  getBoundEntityById,
  unboundValues = {},
  dataSource = {},
  componentVariablesOverwrites,
  componentSettings = { variableDefinitions: {} },
}: {
  variables: Record<string, ComponentPropertyValue>;
  breakpointIds: string[];
  getBoundEntityById: (id: string) => Entry | Asset | undefined;
  unboundValues?: ExperienceUnboundValues;
  dataSource?: ExperienceDataSource;
  componentVariablesOverwrites?: Record<string, ComponentPropertyValue>;
  componentSettings?: ExperienceComponentSettings;
}) => {
  const variableValuesByBreakpoints = breakpointIds.reduce<
    Record<string, Record<string, Exclude<PrimitiveValue, undefined>>>
  >((acc, breakpointId) => {
    return {
      ...acc,
      [breakpointId]: {},
    };
  }, {});

  const defaultBreakpoint = breakpointIds[0];

  for (const [variableName, variableData] of Object.entries(variables)) {
    // handling the special case - cfBackgroundImageUrl variable, which can be bound or unbound
    // so, we need to resolve it here and pass it down as a css property to be converted into the CSS

    // I used .startsWith() cause it can be part of a pattern node
    if (
      variableName === 'cfBackgroundImageUrl' ||
      // TODO: Test this for nested patterns as the name might be just a random hash without the actual name (needs to be validated).
      variableName.startsWith('cfBackgroundImageUrl_')
    ) {
      const width = resolveVariable({
        variableData: variables['cfWidth'],
        defaultBreakpoint,
        componentSettings,
        componentVariablesOverwrites,
      }) as string;

      const options = resolveVariable({
        variableData: variables['cfBackgroundImageOptions'],
        defaultBreakpoint,
        componentSettings,
        componentVariablesOverwrites,
      }) as BackgroundImageOptions;

      if (!options) {
        debug.error(
          '[experiences-core::ssrStyles] Error transforming image asset: Required variable [cfBackgroundImageOptions] missing from component definition',
        );
        continue;
      }

      const imageUrl = resolveBackgroundImageBinding({
        variableData,
        getBoundEntityById,
        unboundValues,
        dataSource,
        componentSettings,
        componentVariablesOverwrites,
        width,
        options,
      });

      if (imageUrl) {
        variableValuesByBreakpoints[defaultBreakpoint][variableName] = imageUrl;
      }

      continue;
    }

    let resolvedVariableData = variableData;

    if (variableData.type === 'ComponentValue') {
      const variableDefinition = componentSettings?.variableDefinitions[variableData.key];
      const defaultValue = variableDefinition.defaultValue as ComponentPropertyValue;
      if (variableDefinition.group === 'style' && defaultValue !== undefined) {
        const overwriteVariableData = componentVariablesOverwrites?.[variableData.key];
        resolvedVariableData = mergeDefaultAndOverwriteValues(defaultValue, overwriteVariableData);
      }
    }

    if (resolvedVariableData.type !== 'DesignValue') {
      continue;
    }

    for (const [breakpointId, variableValue] of Object.entries(
      resolvedVariableData.valuesByBreakpoint,
    )) {
      if (!isValidBreakpointValue(variableValue)) {
        continue;
      }

      variableValuesByBreakpoints[breakpointId] = {
        ...variableValuesByBreakpoints[breakpointId],
        [variableName]: variableValue,
      };
    }
  }

  return variableValuesByBreakpoints;
};

/**
 * Flattens the object from
 * `{ color: { [key]: [value] } }`
 * to
 * `{ 'color.key': [value] }`
 */
export const flattenDesignTokenRegistry = (
  designTokenRegistry: DesignTokensDefinition,
): FlattenedDesignTokens => {
  const flattenObject = (obj: object, prefix = ''): FlattenedDesignTokens => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        // handle border types
        !(typeof value === 'object' && ('width' in value || 'style' in value || 'color' in value))
      ) {
        // Recursively flatten nested objects, but skip objects that look like border definitions
        return {
          ...acc,
          ...flattenObject(value, newKey),
        };
      } else {
        // This is a leaf value (string, number, or border object)
        return {
          ...acc,
          [newKey]: value,
        };
      }
    }, {});
  };

  return flattenObject(designTokenRegistry);
};

function mergeDefaultAndOverwriteValues(
  defaultValue: ComponentPropertyValue,
  overwriteValue?: ComponentPropertyValue,
): ComponentPropertyValue;
function mergeDefaultAndOverwriteValues(
  defaultValue?: ComponentPropertyValue,
  overwriteValue?: ComponentPropertyValue,
): ComponentPropertyValue | undefined;
function mergeDefaultAndOverwriteValues(
  defaultValue?: ComponentPropertyValue,
  overwriteValue?: ComponentPropertyValue,
): ComponentPropertyValue | undefined {
  if (defaultValue?.type === 'DesignValue' && overwriteValue?.type === 'DesignValue') {
    return mergeDesignValuesByBreakpoint(defaultValue, overwriteValue);
  }
  return overwriteValue ?? defaultValue;
}
